import { compact, first } from '@quromedical/utils';
import type {
  ContactPoint,
  Identifier,
  Practitioner,
  Reference,
} from 'fhir/r4';

import { IdentifierCode, system } from '../coding';
import type { WithId } from '../types';
import { createReference } from './id';
import type { CreateReference } from './reference';

/**
 * @deprecated returns an empty string if the value is not defined.
 * Should be update to return undefined or removed
 */
export const getFirstValueWhere = <T extends { value?: string }>(
  condition: (x: T) => boolean,
  arr: Array<T>,
) =>
  arr
    ?.filter(condition)
    .map((c) => c.value)
    .filter((v) => v)[0] || '';

export const mapSecondaryNumbersToTelecom = (
  number?: string,
): ContactPoint | undefined => {
  if (!number) {
    return undefined;
  }

  return {
    system: 'phone',
    value: number,
    rank: 2,
  };
};

export const mapPrimaryNumberToTelecom = (
  phone?: string,
): ContactPoint | undefined => {
  if (!phone) {
    return undefined;
  }

  return {
    system: 'phone',
    value: phone,
    rank: 1,
  };
};

/**
 * Will map phone numbers to a telecom with the assumption that the first number is rank 1 and the
 * rest are of rank 2
 */
export const mapPhoneNumbersToTelecom = (
  numbers?: string[],
): ContactPoint[] => {
  if (!numbers) {
    return [];
  }

  const [primaryNumber, secondaryNumber] = numbers;

  const primary = mapPrimaryNumberToTelecom(primaryNumber);

  const secondary = mapSecondaryNumbersToTelecom(secondaryNumber);

  return compact([primary, secondary]);
};

export const mapSecondaryTelecomToNumbers = (
  practitioner: Practitioner,
): ContactPoint | undefined => {
  if (practitioner.telecom) {
    return first(
      practitioner.telecom.filter((t) => t.rank && t.rank > 1 && t.value),
    );
  }
  return undefined;
};

/**
 * @deprecated returns an empty string if the value is not defined.
 * Should be update to return undefined or removed
 */
export const getEmailFromPractitioner = (practitioner: Practitioner): string =>
  getFirstValueWhere<ContactPoint>(
    (c) => c.system === 'email' && c.rank === 1,
    practitioner.telecom || [],
  );

/**
 * @deprecated returns an empty string if the value is not defined.
 * Should be update to return undefined or removed
 */
export const getPrimaryPhoneNumberFromPractitioner = (
  practitioner: Practitioner,
): string =>
  getFirstValueWhere<ContactPoint>(
    (c) => c.system === 'phone' && c.rank === 1,
    practitioner.telecom || [],
  );

/**
 * @deprecated returns an empty string if the value is not defined.
 * Should be update to return undefined or removed
 */
export const getSecondaryPhoneNumberFromPractitioner = (
  practitioner: Practitioner,
): string =>
  getFirstValueWhere<ContactPoint>(
    (c) => c.system === 'phone' && c.rank === 2,
    practitioner.telecom || [],
  );

export const getPractitionerName = (
  practitioner: Practitioner,
): string | undefined => {
  const name = practitioner.name?.find((n) => n.text);

  return name?.text;
};

/**
 * @deprecated please use `createPractitionerRefWithId` for more correct ID handling
 */
export const createPractitionerRef = (
  practitioner: Practitioner,
): Reference => {
  const display = getPractitionerName(practitioner);

  const id = practitioner.id || '';

  const ref = createReference('Practitioner', id, display);

  return ref;
};

export const createPractitionerRefWithId = (
  practitioner: WithId<Practitioner>,
): CreateReference => {
  const display = getPractitionerName(practitioner);

  const ref = createReference('Practitioner', practitioner.id, display);

  return ref;
};

export const createRegistrationIdentifier = (
  registrationNumber: string,
): Identifier => ({
  system,
  use: 'official',
  type: {
    coding: [
      {
        code: IdentifierCode.registrationNumber,
      },
    ],
  },
  value: registrationNumber,
});
